/**
 * <LoginStartStep> — la première étape de la connexion : l'accueil (organism).
 *
 * L'écran que le livre de marque dessine (« Les fonds », connexion en clair) : une phrase d'accueil,
 * UN champ, une action. L'autre moyen de se connecter est un lien sous le champ. Le parcours ne change
 * pas : un numéro est le choix explicite du code par SMS, il part dès « Continuer » ; une adresse mène
 * à son mot de passe, et rien n'est envoyé avant d'être demandé.
 *
 * Sur un téléphone, l'action est tenue en bas de l'écran, sous le pouce ; sur un grand écran elle
 * remonte sous le champ. La coquille (`<AuthTemplate welcome>`) porte la lueur et le logo.
 *
 * Contrôlé : le mode, les valeurs, l'erreur et l'envoi en cours arrivent en props.
 */
import React from "react"

import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Button } from "../../atoms/Buttons/Button"
import { TextButton } from "../../atoms/Buttons/TextButton"
import { Stack } from "../../atoms/containers/Stack"
import { TextInput, textInputAutofill } from "../../atoms/TextInput"
import { Text } from "../../atoms/typography/Text"
import { AuthHeading } from "../../molecules/AuthHeading"
import { LoginTerms } from "../../molecules/LoginTerms"
import { type UILoginMode } from "../../../ports/primary/ui/types"

export type LoginStartStepProps = {
    /** Le champ montré. L'autre moyen est le lien sous le champ. */
    mode: UILoginMode
    onSwitchMode: () => void
    email: string
    onEmailChange: (email: string) => void
    /** « Saisissez une adresse e-mail valide. » */
    emailError?: string
    phone: string
    onPhoneChange: (phone: string) => void
    /** Le refus du serveur à l'envoi du SMS (numéro illisible, trop de codes demandés…). */
    phoneError?: string
    /** Le SMS part — le bouton principal tourne. */
    sending?: boolean
    /** Le champ numéro prend le focus à l'ouverture — sur le web seulement, c'est le view-model qui le sait. */
    autoFocusPhone?: boolean
    onContinue: () => void
    onOpenTerms?: () => void
    /**
     * « Devenir tuteur » : l'entrée de la candidature tuteur, que l'ancien accueil (le carrousel)
     * portait et que cette étape, devenue l'accueil, reprend (Wajih, 19/10). Absent → pas de lien.
     */
    onBecomeTutor?: () => void
    testID?: string
}

export function LoginStartStep(props: LoginStartStepProps) {
    const {
        mode,
        onSwitchMode,
        email,
        onEmailChange,
        emailError,
        phone,
        onPhoneChange,
        phoneError,
        sending = false,
        autoFocusPhone = false,
        onContinue,
        onOpenTerms,
        onBecomeTutor,
        testID,
    } = props
    const phoneMode = mode === "phone"
    // Une seule porte pour le bouton et pour la touche d'envoi du clavier.
    const canContinue = (phoneMode ? phone : email).trim() !== "" && !sending
    const submit = () => {
        if (canContinue) onContinue()
    }

    return (
        <View style={styles.screen} testID={testID}>
            <Stack gap="8">
                <AuthHeading title="Bonjour, content de vous revoir." />
                <Stack gap="3">
                    {phoneMode ? (
                        <Stack gap="1">
                            <TextInput
                                {...textInputAutofill.phone}
                                size="lg"
                                value={phone}
                                onChangeText={onPhoneChange}
                                placeholder="Numéro de téléphone"
                                accessibilityLabel="Numéro de téléphone"
                                autoFocus={autoFocusPhone}
                                returnKeyType="send"
                                onSubmitEditing={submit}
                                error={phoneError}
                            />
                            <Text size="text-sm" color="text-muted">
                                Pour un élève, le numéro du parent.
                            </Text>
                        </Stack>
                    ) : (
                        <TextInput
                            {...textInputAutofill.email}
                            size="lg"
                            value={email}
                            onChangeText={onEmailChange}
                            placeholder="Adresse e-mail"
                            accessibilityLabel="Adresse e-mail"
                            returnKeyType="next"
                            onSubmitEditing={submit}
                            error={emailError}
                        />
                    )}
                    <Stack align="center">
                        <TextButton
                            label={phoneMode ? "Utiliser mon adresse e-mail" : "Utiliser mon numéro de téléphone"}
                            onPress={onSwitchMode}
                        />
                    </Stack>
                </Stack>
            </Stack>

            <Stack gap="4">
                <Button
                    fullWidth
                    label="Continuer"
                    disabled={!canContinue}
                    loading={phoneMode && sending}
                    onPress={submit}
                />
                <LoginTerms onOpenTerms={onOpenTerms} />
                {onBecomeTutor != null && (
                    <Stack align="center">
                        <TextButton label="Devenir tuteur" onPress={onBecomeTutor} />
                    </Stack>
                )}
            </Stack>
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    // Sur un téléphone, la phrase et le champ en haut, l'action en bas ; sur un grand écran il n'y a
    // pas de pouce à servir, l'action remonte sous le champ.
    screen: {
        flex: 1,
        justifyContent: { xs: "space-between", md: "flex-start" },
        gap: theme.spacing["8"],
    },
}))
